import {
  IContentItemWriterFragment,
  IContentWriterFragment,
  IEnum_Componentcontentcontentwriter_Writeroption,
} from 'src/graphql/generated/hooks'

import { getFullName } from '../utils/getFullName'
import { getSolutionUrl } from '../utils/getSolutionUrl'
import { useDefaultLogo } from './useDefaultLogo'

export interface ContentWriterInformation {
  name: string
  title: string | undefined | null
  company: string | undefined | null
  originalAvatar: string | undefined | null
  logo: string
  description: string | undefined | null
  option: IEnum_Componentcontentcontentwriter_Writeroption
  url: string | undefined | null
}

export type ContentWriterInput = IContentWriterFragment | IContentItemWriterFragment | string | null | undefined

const isContentWriterFragment = (
  writer: IContentWriterFragment | IContentItemWriterFragment | string,
): writer is IContentWriterFragment => typeof writer !== 'string' && 'expert' in writer

export function getContentWriterInformation(writer: ContentWriterInput): ContentWriterInformation | null {
  let originalAvatar: string | undefined | null = null
  let option: IEnum_Componentcontentcontentwriter_Writeroption | null = null
  let url: string | null = null
  let name: string | undefined | null = null
  let title: string | undefined | null = null
  let company: string | undefined | null = null
  let description: string | undefined | null = null

  if (!writer) {
    name = null
  } else if (typeof writer === 'string') {
    name = writer
    option = IEnum_Componentcontentcontentwriter_Writeroption.OneTimeAuthor
  } else if (isContentWriterFragment(writer)) {
    if (writer.writerOption === IEnum_Componentcontentcontentwriter_Writeroption.Expert) {
      const attrs = writer.expert
      const fromParts = getFullName([attrs?.firstName, attrs?.lastName]).trim()
      name = ((attrs?.displayName || fromParts) ?? '').trim() || null
      title = attrs?.title
      company = attrs?.company
      originalAvatar = attrs?.avatar?.url
      option = IEnum_Componentcontentcontentwriter_Writeroption.Expert
      const slug = attrs?.slug
      url = slug ? `/experts/${slug}` : null
      description = attrs?.bio
    } else if (writer.writerOption === IEnum_Componentcontentcontentwriter_Writeroption.Author) {
      name = getFullName([writer.author?.firstName, writer.author?.lastName])
      title = writer.author?.jobTitle
      company = writer.author?.company
      originalAvatar = writer.author?.avatar?.url
      option = IEnum_Componentcontentcontentwriter_Writeroption.Author
      const slug = writer.author?.slug
      url = slug ? `/authors/${slug}` : null
      description = writer.author?.bio
    } else if (writer.writerOption === IEnum_Componentcontentcontentwriter_Writeroption.Vendor) {
      name = getFullName([writer.author?.firstName, writer.author?.lastName])
      originalAvatar = writer.solution?.logo?.url
      option = IEnum_Componentcontentcontentwriter_Writeroption.Vendor
      if (writer.solution) {
        url = getSolutionUrl(writer.solution)
      }
      description = writer.solution?.shortDescription
    } else {
      name = writer.oneTimeAuthorName
      option = IEnum_Componentcontentcontentwriter_Writeroption.OneTimeAuthor
    }
  } else {
    name = writer.name
    originalAvatar = writer.avatar
    option = writer.writerOption
    url = writer.url || null
  }

  const logo = useDefaultLogo(originalAvatar)
  if (!writer || !name || !option) {
    return null
  }
  return {
    name,
    title,
    company,
    logo,
    url,
    originalAvatar,
    description,
    option,
  }
}

export const useContentWriterInformation = (writer: ContentWriterInput): ContentWriterInformation | null => {
  return getContentWriterInformation(writer)
}
